import { useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { TenantLoadingState } from './TenantLoadingState';
import { TenantOptionItem } from './TenantOptionItem';
import styles from './TenantSelector.module.css';
import type { TenantOptionRowModel, TenantRowIndicator, TenantSelectorBodyProps } from './types';

const EXCLUDED_STATUS_INDICATOR: TenantRowIndicator = { label: 'Excluded', indicator: 'hidden' };

export function TenantSelectorBody({
  selection,
  tenantRowData,
  disabled,
  onChange,
  hideToolbar = false,
  isLoading = false,
}: TenantSelectorBodyProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<'name' | 'status'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const tenants = Object.entries(tenantRowData).map(([id, row]) => ({ id, name: row.name }));

  const allCurrentTenantIds = tenants.map(t => t.id);
  const currentTenantIdSet = new Set(allCurrentTenantIds);

  const { selectionType, selectedTenantIds, excludedTenantIds } = selection;
  const selectedTenantIdSet = new Set(selectedTenantIds);
  const excludedTenantIdSet = new Set(excludedTenantIds);

  const allCurrentAndFuture = selectionType === 'all';
  const isTenantSelected = (tenantId: string) =>
    allCurrentAndFuture ? !excludedTenantIdSet.has(tenantId) : selectedTenantIdSet.has(tenantId);
  const allCurrentOnly =
    selectionType === 'selected' &&
    tenants.length > 0 &&
    selectedTenantIds.length === tenants.length &&
    selectedTenantIds.every(id => currentTenantIdSet.has(id));
  const noneSelected = selectedTenantIds.length === 0 && !allCurrentAndFuture;

  const toSelectedTenants = (tenantIds: string[]) =>
    onChange({ selectionType: 'selected', selectedTenantIds: tenantIds, excludedTenantIds: [] });

  const handleSelectAllCurrent = () => toSelectedTenants(allCurrentTenantIds);

  const handleDeselectAll = () => {
    const retainedTenantIds = selectedTenantIds.filter(
      id => tenantRowData[id]?.statusIndicator?.lockReason !== undefined,
    );
    toSelectedTenants(retainedTenantIds);
  };

  const handleTenantSelectionChange = (tenant: { id: string; name: string }) => {
    const tenantId = tenant.id;
    const tenantIsSelected = isTenantSelected(tenantId);
    const selectedItemCannotBeDeselected =
      tenantIsSelected && tenantRowData[tenantId]?.statusIndicator?.lockReason !== undefined;

    if (selectedItemCannotBeDeselected) {
      return;
    }

    if (allCurrentAndFuture) {
      const nextExcludedIds = tenantIsSelected
        ? [...excludedTenantIds, tenantId]
        : excludedTenantIds.filter(id => id !== tenantId);
      onChange({ selectionType: 'all', selectedTenantIds, excludedTenantIds: nextExcludedIds });
    } else {
      const newIds = tenantIsSelected
        ? selectedTenantIds.filter(id => id !== tenantId)
        : [...selectedTenantIds, tenantId];
      toSelectedTenants(newIds);
    }
  };

  const hasSearchQuery = searchQuery.trim().length > 0;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredTenants = hasSearchQuery
    ? tenants.filter(t => t.name.toLowerCase().includes(normalizedSearch))
    : tenants;

  const isTenantExcluded = (tenantId: string) =>
    allCurrentAndFuture && excludedTenantIdSet.has(tenantId);
  const getStatusIndicator = (tenantId: string) =>
    isTenantExcluded(tenantId) ? EXCLUDED_STATUS_INDICATOR : tenantRowData[tenantId]?.statusIndicator;
  const getStatusLabel = (tenantId: string) => (getStatusIndicator(tenantId)?.label ?? '').toLowerCase();

  const sortedTenants = [...filteredTenants].sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aStatus = getStatusLabel(a.id);
    const bStatus = getStatusLabel(b.id);

    const primaryComparison =
      sortColumn === 'name' ? aName.localeCompare(bName) : aStatus.localeCompare(bStatus);
    if (primaryComparison !== 0) {
      return sortDirection === 'asc' ? primaryComparison : primaryComparison * -1;
    }

    const nameComparison = aName.localeCompare(bName);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    return a.id.localeCompare(b.id);
  });

  const handleSortClick = (column: 'name' | 'status') => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }

    setSortColumn('name');
    setSortDirection('asc');
  };

  const isTenantSyncInProgress = Object.values(tenantRowData).some(o => o.isSyncing);
  const selectionControlsDisabled = disabled || isTenantSyncInProgress;
  const activeAriaSort = sortDirection === 'asc' ? 'ascending' : 'descending';
  const renderSortIcon = (column: 'name' | 'status') => {
    if (sortColumn !== column) {
      return <ArrowUpDown className={styles.sortIcon} aria-hidden="true" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className={styles.sortIcon} aria-hidden="true" />
    ) : (
      <ArrowDown className={styles.sortIcon} aria-hidden="true" />
    );
  };

  const rowModelByTenantId = new Map<string, TenantOptionRowModel>();
  for (const tenant of sortedTenants) {
    const id = tenant.id;
    const row = tenantRowData[id];
    rowModelByTenantId.set(id, {
      isSelected: isTenantSelected(id),
      isSyncing: row?.isSyncing ?? false,
      statusIndicator: getStatusIndicator(id),
    });
  }

  function renderListContent() {
    if (isLoading && sortedTenants.length === 0) {
      return (
        <li>
          <TenantLoadingState />
        </li>
      );
    }
    if (sortedTenants.length === 0) {
      return <li>No tenants found</li>;
    }
    return sortedTenants.map(tenant => (
      <TenantOptionItem
        key={tenant.id}
        tenant={tenant}
        row={rowModelByTenantId.get(tenant.id)!}
        disabled={selectionControlsDisabled}
        onTenantSelectionChange={handleTenantSelectionChange}
      />
    ));
  }

  return (
    <>
      {!hideToolbar && (
        <div className={styles.toolbar}>
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            disabled={disabled}
          />
          {!allCurrentAndFuture && !hasSearchQuery && (
            <>
              <button
                type="button"
                onClick={handleSelectAllCurrent}
                disabled={selectionControlsDisabled || allCurrentOnly}
                aria-label="Select all current tenants">
                Select all current
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                disabled={selectionControlsDisabled || noneSelected}
                aria-label="Deselect all tenants">
                Deselect all
              </button>
            </>
          )}
        </div>
      )}

      <div className={styles.columnHeaders}>
        <div role="columnheader" aria-sort={sortColumn === 'name' ? activeAriaSort : 'none'}>
          <button
            type="button"
            className={styles.sortButton}
            onClick={() => handleSortClick('name')}
            aria-label="Sort by business name">
            <span>Business Name</span>
            {renderSortIcon('name')}
          </button>
        </div>
        <div role="columnheader" aria-sort={sortColumn === 'status' ? activeAriaSort : 'none'}>
          <button
            type="button"
            className={styles.sortButton}
            onClick={() => handleSortClick('status')}
            aria-label="Sort by status">
            <span>Status</span>
            {renderSortIcon('status')}
          </button>
        </div>
      </div>

      <ul className={styles.options}>{renderListContent()}</ul>
    </>
  );
}
